import React, { useState, useEffect } from 'react'
import { FiClock, FiCalendar, FiAlertTriangle } from 'react-icons/fi'

interface CountdownTimerProps {
    startTime: string // When the countdown started (application acceptance time)
    duration: string // Duration like "3 days", "1 week", "2 months"
    endDate?: string // Optional specific end date
    className?: string
    compact?: boolean // For compact display mode
}

interface TimeRemaining {
    days: number
    hours: number
    minutes: number
    seconds: number
    total: number
}

// Helper function to parse duration strings like "3 days", "1 week", "2 months"
const parseDuration = (duration: string): number => {
    const lower = duration.toLowerCase().trim()
    const match = lower.match(/(\d+)\s*(day|week|month|hour|minute)s?/)

    if (!match) return 0

    const value = parseInt(match[1])
    const unit = match[2]

    const msPerUnit = {
        minute: 60 * 1000,
        hour: 60 * 60 * 1000,
        day: 24 * 60 * 60 * 1000,
        week: 7 * 24 * 60 * 60 * 1000,
        month: 30 * 24 * 60 * 60 * 1000 // Approximate
    }

    return value * (msPerUnit[unit as keyof typeof msPerUnit] || 0)
}

// Calculate time remaining
const calculateTimeRemaining = (endTime: number): TimeRemaining => {
    const now = Date.now()
    const difference = endTime - now

    if (difference <= 0) {
        return { days: 0, hours: 0, minutes: 0, seconds: 0, total: 0 }
    }

    const days = Math.floor(difference / (1000 * 60 * 60 * 24))
    const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
    const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60))
    const seconds = Math.floor((difference % (1000 * 60)) / 1000)

    return { days, hours, minutes, seconds, total: difference }
}

// Format time remaining for display
const formatTimeRemaining = (timeRemaining: TimeRemaining): string => {
    const { days, hours, minutes, seconds } = timeRemaining

    if (days > 0) {
        return `${days}d ${hours}h ${minutes}m ${seconds}s`
    } else if (hours > 0) {
        return `${hours}h ${minutes}m ${seconds}s`
    } else {
        return `${minutes}m ${seconds}s`
    }
}

// Get urgency level based on percentage of time remaining
const getUrgencyLevel = (timeRemaining: TimeRemaining, totalDuration: number): 'low' | 'medium' | 'high' | 'critical' => {
    const percentageLeft = (timeRemaining.total / totalDuration) * 100

    if (percentageLeft <= 5) return 'critical'
    if (percentageLeft <= 20) return 'high'
    if (percentageLeft <= 50) return 'medium'
    return 'low'
}

const CountdownTimer: React.FC<CountdownTimerProps> = ({
    startTime,
    duration,
    endDate,
    className = '',
    compact = false
}) => {
    const [timeRemaining, setTimeRemaining] = useState<TimeRemaining>({ days: 0, hours: 0, minutes: 0, seconds: 0, total: 0 })
    const [isExpired, setIsExpired] = useState(false)

    useEffect(() => {
        // Calculate end time
        let endTime: number

        if (endDate) {
            endTime = new Date(endDate).getTime()
        } else {
            const startTimestamp = new Date(startTime).getTime()
            const durationMs = parseDuration(duration)
            endTime = startTimestamp + durationMs
        }



        // Update every second
        const timer = setInterval(() => {
            const remaining = calculateTimeRemaining(endTime)
            setTimeRemaining(remaining)

            if (remaining.total <= 0) {
                setIsExpired(true)
                clearInterval(timer)
            }
        }, 1000)

        // Initial calculation
        const remaining = calculateTimeRemaining(endTime)
        setTimeRemaining(remaining)

        if (remaining.total <= 0) {
            setIsExpired(true)
        }

        return () => clearInterval(timer)
    }, [startTime, duration, endDate])

    const urgency = getUrgencyLevel(timeRemaining, parseDuration(duration))

    // Styling based on urgency
    const getUrgencyStyles = () => {
        switch (urgency) {
            case 'critical':
                return 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-800 dark:text-red-200'
            case 'high':
                return 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800 text-orange-800 dark:text-orange-200'
            case 'medium':
                return 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800 text-yellow-800 dark:text-yellow-200'
            default:
                return 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-200'
        }
    }

    if (isExpired) {
        if (compact) {
            return (
                <span className={`text-red-600 dark:text-red-400 font-medium ${className}`}>
                    ⚠️ Expired
                </span>
            )
        }
        return (
            <div className={`p-3 rounded-lg border ${className} bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800`}>
                <div className="flex items-center gap-2 text-red-800 dark:text-red-200">
                    <FiAlertTriangle className="w-4 h-4" />
                    <span className="font-medium text-sm">Deadline Expired</span>
                </div>
                <p className="text-xs text-red-600 dark:text-red-300 mt-1">
                    This gig deadline has passed. Please contact the client for extension.
                </p>
            </div>
        )
    }

    // Compact mode - just show the countdown time
    if (compact) {
        return (
            <span className={`font-mono font-medium ${className}`}>
                {formatTimeRemaining(timeRemaining)}
            </span>
        )
    }

    // Full mode - show the complete UI
    return (
        <div className={`p-3 rounded-lg border ${getUrgencyStyles()} ${className}`}>
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <FiClock className="w-4 h-4" />
                    <span className="font-medium text-sm">Time Remaining</span>
                </div>
                {urgency === 'critical' && (
                    <FiAlertTriangle className="w-4 h-4 text-red-600 dark:text-red-400" />
                )}
            </div>

            <div className="mt-2">
                <div className="text-lg font-mono font-bold">
                    {formatTimeRemaining(timeRemaining)}
                </div>
                <div className="flex items-center gap-1 text-xs opacity-75 mt-1">
                    <FiCalendar className="w-3 h-3" />
                    <span>Duration: {duration}</span>
                </div>
            </div>

            {urgency === 'high' || urgency === 'critical' ? (
                <p className="text-xs mt-2 opacity-90">
                    ⚠️ Deadline approaching! Ensure you complete the work on time.
                </p>
            ) : null}
        </div>
    )
}

export default CountdownTimer